import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { safeFetch } from "@/lib/api";
import { connectDashboardWS } from "@/lib/websocket";
import { META } from "@/lib/metadata";
import { toast } from "sonner";

const AppCtx = createContext(null);

const EMPTY_FILTERS = {
  venture: "", site: "", category: "", subcategory: "", severity: "",
  state: "", platform: "", q: "", start: "", end: "", window: "30d",
};

export function AppProvider({ children }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedDetail, setSelectedDetail] = useState(null); // { type, payload }
  const [selectedPost, setSelectedPost] = useState(null);

  const [kpis, setKpis] = useState(null);
  const [posts, setPosts] = useState([]);
  const [livePosts, setLivePosts] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [locations, setLocations] = useState({ states: [], sites: [] });
  const [severityDist, setSeverityDist] = useState([]);
  const [platformDist, setPlatformDist] = useState([]);
  const [categoryDist, setCategoryDist] = useState([]);
  const [ventureDist, setVentureDist] = useState([]);
  const [ventureBreakdown, setVentureBreakdown] = useState([]);
  const [topSites, setTopSites] = useState([]);
  const [sentimentTrend, setSentimentTrend] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [tickerSignals, setTickerSignals] = useState([]);
  const [wsStatus, setWsStatus] = useState({ connected: false });
  const [source, setSource] = useState("live");
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const paramsFromFilters = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [filters]);

  // ── Request tracking to prevent race conditions ──
  const lastReqId = useRef(0);
  const debounceTimer = useRef(null);
  const refreshTimer = useRef(null);
  // Track post count via ref to avoid stale closures in loadMore
  const postsCountRef = useRef(0);
  // Track loadingMore in ref to prevent double-fire from IntersectionObserver
  const loadingMoreRef = useRef(false);

  // Keep postsCountRef in sync
  useEffect(() => { postsCountRef.current = posts.length; }, [posts.length]);

  const loadAll = useCallback(async (signal) => {
    const reqId = ++lastReqId.current;

    setWsStatus(prev => ({ ...prev, loading: true }));

    try {
      const res = await safeFetch("/dashboard/all", {
        params: paramsFromFilters,
        signal,
        headers: { "X-Priority": "High" } // Custom header to hint backend if needed
      });

      // Stale response guard
      if (reqId !== lastReqId.current) return;

      if (res.data) {
        const d = res.data;
        React.startTransition(() => {
          setKpis(d.kpis || {});
          setHierarchy(d.hierarchy || []);
          setCategoryDist(d.categoryDist || []);
          setVentureDist(d.ventureDist || []);

          const initialPosts = d.latestPosts || [];
          setPosts(initialPosts);
          setLivePosts(initialPosts);

          // Randomize ticker to prevent platform bias from bulk ingestion
          const diverseTicker = [...initialPosts].sort(() => 0.5 - Math.random()).slice(0, 24);
          setTickerSignals(diverseTicker);

          setSeverityDist(d.severityDist || []);
          setPlatformDist(d.platformDist || []);
          setTopSites(d.topSites || []);
          setSentimentTrend(d.sentimentTrend || []);
          setVentureBreakdown(d.ventureBreakdown || []);

          const mappedSites = (d.topSites || []).map((s, idx) => {
            const coords = META.STATE_COORDS[s.state] || [20.0, 78.0];
            return {
              id: idx, site: s.site, state: s.state, venture: s.venture,
              signals: s.signals, critical: s.critical, avg_risk: s.avg_risk,
              lat: coords[0] + (Math.random() - 0.5) * 0.4,
              lng: coords[1] + (Math.random() - 0.5) * 0.4
            };
          });
          setLocations({ states: d.stateDist || [], sites: mappedSites });
          setWsStatus(prev => ({ ...prev, loading: false }));
        });
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error("Dashboard Load Error:", err);
        setWsStatus(prev => ({ ...prev, loading: false }));
      }
    }

    // Secondary fetch for full post dataset — uses SAME signal to auto-cancel on filter change
    try {
      const postsRes = await safeFetch("/posts", { params: { ...paramsFromFilters, limit: 100 }, signal });
      if (reqId !== lastReqId.current) return;
      const pData = postsRes.data?.items || [];
      const total = postsRes.data?.total || 0;
      if (pData.length > 0) {
        React.startTransition(() => {
          setPosts(pData);
          setLivePosts(pData);
          setHasMore(false); // Strictly limit to 100 total for maximum performance
        });
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error("Posts fetch error:", err);
      }
    }

    setSource("live");
  }, [paramsFromFilters]);

  // ── Data load effect with smart debouncing ──
  useEffect(() => {
    const controller = new AbortController();
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    const isImmediate = !paramsFromFilters.q && !paramsFromFilters.start && !paramsFromFilters.end;

    if (isImmediate) {
      loadAll(controller.signal);
    } else {
      debounceTimer.current = setTimeout(() => {
        loadAll(controller.signal);
      }, 120);
    }

    return () => {
      controller.abort();
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [paramsFromFilters, loadAll]);

  // ── Master Buffer System for Live Data Stability ──
  const messageBuffer = useRef([]);
  const statsBuffer = useRef({ total: 0, critical: 0, high: 0 });
  const flushInterval = useRef(null);

  const flushBuffer = useCallback(() => {
    if (messageBuffer.current.length === 0 && statsBuffer.current.total === 0) return;

    const arr = [...messageBuffer.current];
    const stats = { ...statsBuffer.current };
    
    // Reset buffers
    messageBuffer.current = [];
    statsBuffer.current = { total: 0, critical: 0, high: 0 };

    React.startTransition(() => {
      // 1. KPI Update (Authoritative stats)
      setKpis((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          total_posts: (prev.total_posts || 0) + stats.total,
          critical_count: (prev.critical_count || 0) + stats.critical,
          high_risk_count: (prev.high_risk_count || 0) + stats.critical + stats.high,
          posts_last_24h: (prev.posts_last_24h || 0) + stats.total
        };
      });

      // 2. Feed Update (Incremental)
      if (arr.length > 0) {
        setLivePosts((prev) => {
          const incomingIds = new Set(arr.map(x => x.id));
          const filteredOld = prev.filter(x => !incomingIds.has(x.id));
          return [...arr, ...filteredOld].slice(0, 100);
        });
        
        // Analytical 'posts' update (throttled to preserve chart stability)
        setPosts((prev) => {
          const incomingIds = new Set(arr.map(x => x.id));
          const filteredOld = prev.filter(x => !incomingIds.has(x.id));
          return [...arr, ...filteredOld].slice(0, 100);
        });

        setTickerSignals((prev) => {
          const incomingIds = new Set(arr.map(x => x.id));
          const filteredOld = prev.filter(x => !incomingIds.has(x.id));
          return [...arr, ...filteredOld].slice(0, 24);
        });

        // Distribution Updates
        setSeverityDist((prev) => {
          const next = [...prev];
          arr.forEach(p => {
            const s = (p.severity || "low").toLowerCase();
            const idx = next.findIndex(x => x.severity === s);
            if (idx > -1) next[idx] = { ...next[idx], count: (next[idx].count || 0) + 1 };
            else next.push({ severity: s, count: 1 });
          });
          return next;
        });

        setCategoryDist((prev) => {
          const next = [...prev];
          arr.forEach(p => {
            const idx = next.findIndex(x => x.category === p.category);
            if (idx > -1) {
              next[idx] = {
                ...next[idx],
                signals: (next[idx].signals || 0) + 1,
                critical: (next[idx].critical || 0) + (p.severity === 'critical' ? 1 : 0)
              };
            }
          });
          return next;
        });
      }
    });

    // Debounced full refresh for heavy background aggregations
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => {
      const refreshController = new AbortController();
      loadAll(refreshController.signal);
    }, 2000); // 2s window for analytical stability
  }, [loadAll]);

  // Set up the heartbeat for flushing the buffer
  useEffect(() => {
    flushInterval.current = setInterval(flushBuffer, 1500); // Flush every 1.5s
    return () => clearInterval(flushInterval.current);
  }, [flushBuffer]);

  // ── WebSocket connection ──
  useEffect(() => {
    const ws = connectDashboardWS({
      onOpen: () => {
        setWsStatus({ connected: true });
        const controller = new AbortController();
        loadAll(controller.signal);
      },
      onClose: () => setWsStatus({ connected: false }),
      onMessage: (msg) => {
        if (!msg || !msg.event) return;

        if (msg.event === "NEW_POSTS") {
          const arr = Array.isArray(msg.payload) ? msg.payload : [msg.payload];
          const stats = msg.stats || { total: arr.length, critical: 0, high: 0 };
          
          // Buffer the updates instead of triggering immediate rerender
          messageBuffer.current.push(...arr);
          statsBuffer.current.total += stats.total;
          statsBuffer.current.critical += stats.critical;
          statsBuffer.current.high += stats.high;

        } else if (msg.event === "HIGH_RISK_ALERT") {
          const alert = { id: `a_${Date.now()}`, at: new Date().toISOString(), ...msg.payload };
          const matches = !paramsFromFilters.venture || alert.venture === paramsFromFilters.venture;
          const siteMatches = !paramsFromFilters.site || alert.site === paramsFromFilters.site;
          
          // Alerts bypass buffer for immediate actionability
          setAlerts((prev) => [alert, ...prev].slice(0, 50));
          setTickerSignals((prev) => [{ ...msg.payload, _alert: true }, ...prev].slice(0, 24));

          try {
            const audio = new Audio("https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3");
            audio.volume = 0.4;
            audio.play().catch(() => { });
          } catch (e) { }

          toast.error(`CRITICAL THREAT: ${alert.site || alert.venture || "Unknown Site"}`, {
            description: (
              <div>
                <p>{alert.content?.slice(0, 120)}...</p>
                {(!matches || !siteMatches) && (
                  <p className="mt-2 text-xs opacity-80 border-t border-white/20 pt-1">
                    Note: This threat is outside your current active filters.
                  </p>
                )}
              </div>
            ),
            duration: 15000,
            important: true,
            onClick: () => {
              setSelectedPost(alert);
              if (!matches || !siteMatches) setFilters(EMPTY_FILTERS);
            },
            style: { background: "#DC2626", color: "#FFFFFF", border: "1px solid #DC2626", borderRadius: "0px", cursor: "pointer" },
          });
        }
      },
    });
    return () => {
      ws.close();
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [loadAll, paramsFromFilters]);

  // ── Pagination: loadMore with ref-based guards ──
  const loadMore = useCallback(async () => {
    // Double-fire guard using ref (not state — state is async)
    if (loadingMoreRef.current || !hasMore) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);

    try {
      // Use ref for skip to avoid stale closure
      const currentCount = postsCountRef.current;
      const res = await safeFetch("/posts", {
        params: { ...paramsFromFilters, limit: 100, skip: currentCount }
      });

      const newItems = res.data?.items || [];
      const total = res.data?.total || 0;
      if (newItems.length === 0) {
        setHasMore(false);
      } else {
        React.startTransition(() => {
          setPosts((prev) => {
            const existingIds = new Set(prev.map(x => x.id));
            const uniqueNew = newItems.filter(x => !existingIds.has(x.id));
            return [...prev, ...uniqueNew];
          });
          setLivePosts((prev) => {
            const existingIds = new Set(prev.map(x => x.id));
            const uniqueNew = newItems.filter(x => !existingIds.has(x.id));
            return [...prev, ...uniqueNew];
          });
          // If we received fewer items than requested, no more pages
          if (newItems.length < 100) {
            setHasMore(false);
          }
        });
      }
    } catch (err) {
      console.error("Load More Error:", err);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [hasMore, paramsFromFilters]);

  const clearFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setHasMore(true);
  }, []);

  const patchFilters = useCallback((patch) => {
    setFilters((f) => ({ ...f, ...patch }));
    setHasMore(true);
  }, []);

  // ── Stable context value (memoized to prevent unnecessary re-renders of consumers) ──
  const value = useMemo(() => ({
    filters, setFilters, patchFilters, clearFilters,
    activeTab, setActiveTab,
    selectedDetail, setSelectedDetail,
    selectedPost, setSelectedPost,
    kpis, posts, livePosts, hierarchy, locations, severityDist,
    platformDist, categoryDist, ventureDist, ventureBreakdown, topSites, sentimentTrend,
    alerts, tickerSignals, wsStatus, source,
    META,
    refresh: loadAll,
    loadMore, hasMore, loadingMore
  }), [
    filters, patchFilters, clearFilters,
    activeTab, selectedDetail, selectedPost,
    kpis, posts, livePosts, hierarchy, locations, severityDist,
    platformDist, categoryDist, ventureDist, ventureBreakdown, topSites, sentimentTrend,
    alerts, tickerSignals, wsStatus, source,
    loadAll, loadMore, hasMore, loadingMore
  ]);

  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}

export const useApp = () => useContext(AppCtx);
